import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { typeScale, surfaceTokens } from "../config/theme";
import { useCountUp } from "../helpers/useCountUp";

interface StatCardProps {
  metric: React.ReactNode;
  label: string;
  accentColor?: string;
}

/**
 * Wrapper that animates a numeric metric with count-up.
 */
function AnimatedNumber({ value }: { value: number }) {
  const animated = useCountUp(value);
  return <>{Math.round(animated)}</>;
}

/**
 * Compact KPI stat card used in the PageHeader KPI strip.
 * Renders a large metric value above a small label, with a left border accent.
 * Numeric metrics animate with a count-up effect on mount/change.
 */
export default function StatCard({ metric, label, accentColor }: StatCardProps) {
  const { palette } = useTheme();
  // Default accent stays legible on dark (brandLight -> #6db85b in dark mode).
  const accent = accentColor ?? surfaceTokens(palette.mode === "dark").brandLight;
  const isNumeric = typeof metric === "number" && isFinite(metric);

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${accent}`,
        pl: 1.5,
        py: 0.5,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography
        sx={{
          // metric token, with a slightly smaller size on mobile
          fontSize: { xs: "1.22rem", md: typeScale.metric.fontSize },
          fontWeight: typeScale.metric.fontWeight,
          lineHeight: typeScale.metric.lineHeight,
          color: "text.primary",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          maxWidth: { xs: 140, md: "none" },
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {isNumeric ? <AnimatedNumber value={metric as number} /> : metric}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          mt: 0.25,
          whiteSpace: "nowrap",
          maxWidth: { xs: 140, md: "none" },
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
